/**
 * usePWAUpdate — silent auto-update for the Dukkanty PWA.
 *
 * Two complementary strategies:
 *   1. controllerchange (Android/Chrome): when a new SW takes control,
 *      reload immediately so the new JS bundle is served.
 *   2. version.json polling (iOS + Android fallback): on every tab focus,
 *      fetch /version.json and reload if the build stamp differs from
 *      the one baked into this bundle at build time.
 *
 * No banner, no user action needed — transparent to the user.
 */

import { useEffect } from 'react'

// Injected by Vite at build time (see vite.config.ts → define)
declare const __APP_VERSION__: string
const BUILD_VERSION =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'

export function usePWAUpdate() {
  // ── Strategy 1: SW controllerchange (Android / Chrome) ──────────
  useEffect(() => {
    if (!navigator.serviceWorker) return
    if (import.meta.env.DEV) return // Skip in dev mode

    const onControllerChange = () => {
      console.log('[PWA] New SW took control — reloading for latest version')
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }
  }, [])

  // ── Strategy 2: version.json check (iOS + Android fallback) ─────
  useEffect(() => {
    if (import.meta.env.DEV) return // Skip in dev mode — avoids reload loops

    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json?t=' + Date.now(), {
          cache: 'no-store',
        })
        if (!res.ok) return

        const { version } = (await res.json()) as { version: string }

        if (version && version !== BUILD_VERSION) {
          console.log(
            `[PWA] New build detected (${BUILD_VERSION} → ${version}), reloading`
          )
          window.location.reload()
        }
      } catch {
        // Offline or fetch failed — stay on current version silently
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion()
    }

    // Check on every tab focus
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Also check once shortly after startup (don't block initial render)
    const timer = setTimeout(checkVersion, 3000)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearTimeout(timer)
    }
  }, [])
}
